import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@components/ui/Card';
import Badge from '@components/ui/Badge';
import { 
  Calendar, 
  BookOpen, 
  FileText, 
  TrendingUp, 
  Users, 
  ArrowRight,
  Clock
} from 'lucide-react';
import { upcomingEvents } from '@data/siteData';
import type { Booking, Article } from '../../types';

// Mock data for dashboard stats
const mockBookings: Booking[] = [
  {
    id: '1',
    eventId: '1',
    name: 'Max Mustermann',
    email: 'max@example.com',
    participants: 2,
    status: 'confirmed',
    bookingReference: 'BK-2026-001',
    createdAt: '2026-01-15T10:30:00Z',
  },
  {
    id: '2',
    eventId: '2',
    name: 'Anna Schmidt',
    email: 'anna@example.com',
    participants: 4,
    status: 'pending',
    bookingReference: 'BK-2026-002',
    createdAt: '2026-01-18T14:20:00Z',
  },
  {
    id: '3',
    eventId: '3',
    name: 'Peter Müller',
    email: 'peter@example.com',
    participants: 1,
    status: 'confirmed',
    bookingReference: 'BK-2026-003',
    createdAt: '2026-01-20T09:15:00Z',
  },
];

const mockArticles: Article[] = [
  {
    id: '1',
    title: 'Neue Ausstellung im REM',
    content: '...',
    category: 'news',
    status: 'published',
    publishedAt: '2026-01-10T12:00:00Z',
    createdAt: '2026-01-05T10:00:00Z',
  },
  {
    id: '2',
    title: 'Geschichte der Sternwarte',
    content: '...',
    category: 'history',
    status: 'published',
    publishedAt: '2026-01-08T14:00:00Z',
    createdAt: '2026-01-01T09:00:00Z',
  },
  {
    id: '3',
    title: 'Astronomie-Workshop für Kinder',
    content: '...',
    category: 'astronomy',
    status: 'draft',
    createdAt: '2026-01-20T16:00:00Z',
  },
];

interface StatCardProps {
  title: string;
  value: number;
  description: string;
  icon: React.ElementType;
  href: string;
  trend?: string;
  trendUp?: boolean;
}

function StatCard({ title, value, description, icon: Icon, href, trend, trendUp }: StatCardProps) {
  return (
    <Card hover>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-slate-400 text-sm font-medium">{title}</p>
            <p className="text-3xl font-bold text-white mt-2">{value}</p>
            <p className="text-slate-500 text-sm mt-1">{description}</p>
            {trend && (
              <div className={`flex items-center gap-1 mt-2 text-sm ${trendUp ? 'text-green-400' : 'text-slate-400'}`}>
                <TrendingUp className="w-4 h-4" />
                <span>{trend}</span>
              </div>
            )}
          </div>
          <div className="w-12 h-12 rounded-xl bg-blue-600/10 flex items-center justify-center">
            <Icon className="w-6 h-6 text-blue-400" />
          </div>
        </div>
        <Link 
          to={href}
          className="flex items-center gap-2 mt-4 text-sm text-blue-400 hover:text-blue-300 transition-colors"
        >
          Details anzeigen
          <ArrowRight className="w-4 h-4" />
        </Link>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const stats = useMemo(() => {
    const upcomingCount = upcomingEvents.length;
    const bookingsCount = mockBookings.length;
    const confirmedBookings = mockBookings.filter(b => b.status === 'confirmed').length;
    const articlesCount = mockArticles.length;
    const publishedArticles = mockArticles.filter(a => a.status === 'published').length;

    return {
      upcomingCount,
      bookingsCount,
      confirmedBookings,
      articlesCount,
      publishedArticles,
    };
  }, []);

  const recentBookings = useMemo(() => {
    return [...mockBookings]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 5);
  }, []);

  const upcomingEventsList = useMemo(() => {
    return [...upcomingEvents]
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(0, 5);
  }, []);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <Badge variant="success">Bestätigt</Badge>;
      case 'pending':
        return <Badge variant="warning">Ausstehend</Badge>;
      case 'cancelled':
        return <Badge variant="error">Storniert</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Dashboard</h1>
        <p className="text-slate-400 mt-1">
          Übersicht über alle Aktivitäten der Sternwarte
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <StatCard
          title="Veranstaltungen"
          value={stats.upcomingCount}
          description="Aktuelle und kommende Termine"
          icon={Calendar}
          href="/admin/events"
          trend="+2 diese Woche"
          trendUp={true}
        />
        <StatCard
          title="Buchungen"
          value={stats.bookingsCount}
          description={`${stats.confirmedBookings} bestätigt`}
          icon={BookOpen}
          href="/admin/bookings"
          trend="+5 neue"
          trendUp={true}
        />
        <StatCard
          title="Artikel"
          value={stats.articlesCount}
          description={`${stats.publishedArticles} veröffentlicht`}
          icon={FileText}
          href="/admin/articles"
          trend="1 Entwurf"
          trendUp={false}
        />
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Bookings */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Neueste Buchungen</CardTitle>
              <CardDescription>Kürzlich eingegangene Anmeldungen</CardDescription>
            </div>
            <Link 
              to="/admin/bookings"
              className="text-sm text-blue-400 hover:text-blue-300"
            >
              Alle anzeigen
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-800">
              {recentBookings.map((booking) => (
                <div key={booking.id} className="px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center">
                      <Users className="w-5 h-5 text-slate-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{booking.name}</p>
                      <p className="text-slate-400 text-sm">{booking.participants} Teilnehmer</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-slate-500 text-sm">{formatDate(booking.createdAt)}</span>
                    {getStatusBadge(booking.status)}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Events */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Kommende Veranstaltungen</CardTitle>
              <CardDescription>Nächste Termine in der Übersicht</CardDescription>
            </div>
            <Link 
              to="/admin/events"
              className="text-sm text-blue-400 hover:text-blue-300"
            >
              Alle anzeigen
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-800">
              {upcomingEventsList.map((event) => (
                <div key={event.id} className="px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-blue-900/30 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium truncate max-w-[200px]">{event.title}</p>
                      <p className="text-slate-400 text-sm">{event.type}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-medium">{formatDate(event.date)}</p>
                    <p className="text-slate-400 text-sm">{event.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
